import { useCallback } from 'react'

import { ComplexTable, TableCard } from '@shared/components/tables'
import { Header } from '@shared/components/tables/TableCard/Header/Header'
import { useSettlementCreditRepaymentTransfersLazyQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { useSettlementCreditRepaymentTransfersColumns } from './RepaymentsTable.Columns'

import type { OnFetchDataProps } from '@circlefin/components/lib/AdvancedTable'
import type { SettlementCreditRepaymentTransfer } from '@shared/graphql'

const { Body } = TableCard

export interface RepaymentsTableProps {
  /**
   * Settlement Credit Transfer ID.
   */
  transferId?: string
  /**
   * Payment Job ID.
   */
  jobId?: string
  /**
   * Controls the amount of lines in the table for each paginated page.
   */
  pageSize?: number
}

export const RepaymentsTable: React.FC<RepaymentsTableProps> = ({
  transferId,
  jobId,
  pageSize = 10,
}) => {
  const { t } = useTranslation('lineOfCredit')
  const [fetchTransfers, { called, data, loading, error, refetch }] =
    useSettlementCreditRepaymentTransfersLazyQuery({
      variables: {
        page: { pageSize },
        filter: { transferId, jobId },
      },
      fetchPolicy: 'cache-and-network',
    })

  const columns = useSettlementCreditRepaymentTransfersColumns(!!jobId)

  const handleFetchData = useCallback(
    ({
      currentPageLink,
    }: OnFetchDataProps<SettlementCreditRepaymentTransfer>) => {
      void fetchTransfers({
        variables: {
          page: currentPageLink
            ? {
                pageSize: currentPageLink.pageSize,
                from: currentPageLink.from,
                to: currentPageLink.to,
                pageAfter: currentPageLink.pageAfter,
                pageBefore: currentPageLink.pageBefore,
              }
            : { pageSize },
          filter: {
            transferId,
            jobId,
          },
        },
      })
    },
    [fetchTransfers, pageSize, transferId, jobId],
  )

  return (
    <TableCard paddingVariant="none" disableCardAppearance>
      <Header>
        <span className="type-h-title-md">{t('details.repayments.title')}</span>
      </Header>
      <Body>
        <ComplexTable<SettlementCreditRepaymentTransfer>
          columns={columns}
          data={data?.settlementCreditRepaymentTransfers}
          data-testid="repayments-table"
          empty={{ subtitle: t('repaymentsTable.empty') }}
          enableSorting={false}
          error={error}
          loading={!called || loading}
          onFetchData={handleFetchData}
          pageSize={pageSize}
          pagination="links"
          retry={refetch}
          manualFiltering
        />
      </Body>
    </TableCard>
  )
}
